import circle from '@turf/circle'
import type { Feature, GeoJsonProperties, Polygon } from 'geojson'

type CircleFeatureProperties = GeoJsonProperties & {
  appDrawMode?: 'draw_circle' | null
  center: [number, number]
  isCircle: true
  mode?: 'circle'
  radiusInKm: number
  radiusKilometers?: number
}

export type GoogleCircleFeature = Feature<Polygon, CircleFeatureProperties>

export function isStoredCircleFeature(
  feature: Feature | null | undefined
): feature is GoogleCircleFeature {
  if (!feature || feature.geometry?.type !== 'Polygon') return false

  const properties = feature.properties as CircleFeatureProperties | null

  return Boolean(
    properties?.isCircle &&
      Array.isArray(properties.center) &&
      typeof properties.radiusInKm === 'number'
  )
}

export function circleToFeature(
  googleCircle: google.maps.Circle,
  featureId?: Feature['id']
): GoogleCircleFeature {
  const center = googleCircle.getCenter()
  const radiusInKm = googleCircle.getRadius() / 1000

  if (!center) {
    throw new Error('Google circle center is required')
  }

  const lng = center.lng()
  const lat = center.lat()
  const polygon = circle([lng, lat], radiusInKm, {
    steps: 64,
    units: 'kilometers',
  })

  return {
    type: 'Feature',
    id: featureId,
    geometry: polygon.geometry,
    properties: {
      appDrawMode: 'draw_circle',
      center: [lng, lat],
      isCircle: true,
      mode: 'circle',
      radiusInKm,
      radiusKilometers: radiusInKm,
    },
  }
}

export function featureToCircleOptions(feature: Feature) {
  if (!isStoredCircleFeature(feature)) {
    throw new Error('Feature is not a stored circle feature')
  }

  return {
    center: {
      lng: feature.properties.center[0],
      lat: feature.properties.center[1],
    },
    radiusMeters: feature.properties.radiusInKm * 1000,
  }
}
